/**
 * Opcje listy „Pole daty numeracji" w edytorach typów rekordu — wspólna budowa dla typów
 * dokumentów (tenant i panel ITE) i typów zadań. Wołający podaje źródło pól rdzennych
 * i dodatkowych oraz różnice (opcja wiodąca, pominięcie jednego pola rdzennego).
 */

import { isNumberingBaseDateFieldType } from '@saas/shared';
import type { TFunction } from 'i18next';

/** Opcja listy „Pole daty numeracji". */
export interface NumberingBaseFieldOption {
  /** Slug pola. */
  value: string;
  label: string;
}

/** Pole rdzenne (stała z `@saas/shared`): slug + klucz i18n etykiety. */
export interface NumberingBaseCoreFieldLike {
  slug: string;
  labelKey: string;
}

/** Pole dodatkowe z katalogu pól — tylko to, czego potrzebuje filtr. */
export interface NumberingBaseExtFieldLike {
  slug: string;
  name: string;
  fieldType: string;
  /** Brak = pole dodatkowe; `true` = pole rdzenne katalogu (patrz `skipCatalogCoreFields`). */
  isCore?: boolean;
}

export interface BuildNumberingBaseFieldOptionsInput {
  t: TFunction;
  coreFields: readonly NumberingBaseCoreFieldLike[];
  /** `undefined` = katalog niewczytany — lista ma wtedy same opcje rdzenne (i wiodącą). */
  extFields: readonly NumberingBaseExtFieldLike[] | undefined;
  /** Opcja przed polami rdzennymi (np. „domyślnie"). */
  leadingOption?: NumberingBaseFieldOption;
  /** Slug pola rdzennego, które nie jest osobną opcją (nadal wyklucza pola dodatkowe o tym slugu). */
  omitCoreSlug?: string;
  /**
   * `true` = pola z katalogu oznaczone `isCore` nie trafiają na listę (rdzenne daty dają stałe
   * `coreFields`); `false` = trafiają, jeśli są datą / datą i czasem o slugu spoza `coreFields` —
   * zachowanie zastane listy typów dokumentów u tenanta; serwer odrzuca taki wybór (przyjmuje tylko
   * `coreFields` i pola dodatkowe). Bez wartości domyślnej — wołający wybiera świadomie.
   */
  skipCatalogCoreFields: boolean;
}

/**
 * Kolejność: opcja wiodąca, pola rdzenne (bez `omitCoreSlug`), pola dodatkowe typu data / data
 * i czas o slugu niepokrywającym się z żadnym polem rdzennym.
 */
export function buildNumberingBaseFieldOptions({
  t,
  coreFields,
  extFields,
  leadingOption,
  omitCoreSlug,
  skipCatalogCoreFields,
}: BuildNumberingBaseFieldOptionsInput): NumberingBaseFieldOption[] {
  const coreSlugs: ReadonlySet<string> = new Set(coreFields.map((f) => f.slug));
  const coreOptions: NumberingBaseFieldOption[] = coreFields
    .filter((f) => f.slug !== omitCoreSlug)
    .map((f) => ({ value: f.slug, label: t(f.labelKey) }));
  const extOptions: NumberingBaseFieldOption[] = (extFields ?? [])
    .filter(
      (f) =>
        !(skipCatalogCoreFields && f.isCore) &&
        isNumberingBaseDateFieldType(f.fieldType) &&
        !coreSlugs.has(f.slug),
    )
    .map((f) => ({ value: f.slug, label: f.name }));
  return [...(leadingOption ? [leadingOption] : []), ...coreOptions, ...extOptions];
}
